import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { getCachedImage } from '../utils/imageCache';
import PriceDisplay from './PriceDisplay';
import LdPlaceHolder from '../assets/ldplaceholder.png';
import {UNIT_LABELS_SINGULAR, UNIT_LABELS_PLURAL} from '../utils/units.js';

// const UNIT_LABELS_PLURAL = {
//   'unidad': 'unidades',
//   'kg': 'kilogramos',
//   'lt': 'litros',
//   'm2': 'metros cuadrados',
//   'mt': 'metros',
//   'cm': 'centímetros',
//   'docena': 'docenas',
//   'caja': 'cajas',
//   'pack': 'packs',
//   'hectarea': 'hectáreas'
// };


const ProductCard = ({ product }) =>
    {
      const navigate = useNavigate();
    
      return (
        <Card
          onClick={() => navigate(`/product/${product._id}`)}
          to={`/product/${product._id}`}
          href={`/product/${product._id}`}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 20px rgba(0,0,0,0.12)',
              '& .product-image': {
                transform: 'scale(1.05)'
              }
            }
          }}
        >
          {product.distributor?.verified && (
            <Tooltip
              title="Este distribuidor ha sido verificado por Liderdist"
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <VerifiedIcon
                  color="primary"
                  sx={{
                    fontSize: '20px',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                  }}
                />
              </Box>
            </Tooltip>
          )}
    
          <Box sx={{
            position: 'relative',
            pt: '75%',
            overflow: 'hidden'
          }}>
            <CardMedia
              component="img"
              image={product.imageUrls?.[0] ? getCachedImage(product.imageUrls?.[0].replace('upload/', 'upload/q_auto/')) : LdPlaceHolder}
              alt={product.name}
              className="product-image"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                p: 2,
                color: 'white'
              }}
            >
              <Typography variant="h6" sx={{
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                mb: 0.5
              }}>
                {product.name}
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                    {product.distributorName}
                  </Typography>
                  {product.distributorVerified && (
                    <VerifiedIcon
                      sx={{
                        fontSize: '16px',
                        color: '#2196f3',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 1,
                    backgroundColor: 'rgba(255, 88, 51, 0.8)',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'inline-block',
                    maxWidth: '100%',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    fontSize: '0.75rem'
                  }}
                >
                  {product.category}
                </Typography>
              </Box>
            </Box>
          </Box>
    
          <CardContent sx={{
            flexGrow: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Price and category */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1.5
            }}>
              <PriceDisplay
                basePrice={product.basePrice}
                priceBreaks={product.priceBreaks}
                hidePrice={product.hidePrice}
                unit={product.unit}
                hasMainPrice={true}
              />
    
            </Box>
    
            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: (product.hasPriceBreaks) ? 4 : 8,
                WebkitBoxOrient: 'vertical',
                mb: 2,
                minHeight: '60px'
              }}
            >
              {product.description}
            </Typography>
    
            {/* Spacer to push location and stock to bottom */}
            <Box sx={{ flexGrow: 1 }} />
    
            {/* Location and stock at bottom */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LocationOnIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Tooltip
                  title={
                    product.shippingZones?.length > 1
                      ? <Box sx={{ p: 1 }}>
                        {product.shippingZones.join(', ')}
                      </Box>
                      : ''
                  }
                  arrow
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      cursor: product.shippingZones?.length > 1 ? 'pointer' : 'default',
                      '&:hover': {
                        textDecoration: product.shippingZones?.length > 1 ? 'underline' : 'none'
                      },
                      lineHeight: 1.2,
                      display: 'block',
                      margin: 0
                    }}
                  >
                    {product.shippingZones?.length > 0
                      ? `${product.shippingZones[0]}${product.shippingZones.length > 1 ? ` +${product.shippingZones.length - 1} más` : ''}`
                      : 'Ubicación no especificada'
                    }
                  </Typography>
                </Tooltip>
              </Box>
              {product.availableAmount > 0 && (
                <Chip
                  size="small"
                  label={`Stock: ${product.availableAmount}`}
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.08)',
                    color: 'success.main',
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      );
    };
export default ProductCard; 